import { AGE_OPTIONS, CASE_PRESET_TYPE, FIRST_STEP } from "./caseContent";
import { TCPA_LANGUAGE, siteText } from "../config";
import { isValidEmail, isValidZip } from "../publicFormValidators";
import { isValidUSPhone } from "../publicFormPhone";
import { readTrustedFormCert } from "../trustedForm";

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const YES_NO = [{ value: true, label: "Yes" }, { value: false, label: "No" }];

export function initialAnswers(slug) {
  return {
    detail: "",
    case_type: CASE_PRESET_TYPE[slug] || "",
    incident_age_bucket: "",
    physically_injured: null,
    medical_treatment: null,
    at_fault: null,
    at_fault_party_insured: null,
    currently_represented: null,
    accident_description: "",
    first_name: "", last_name: "", phone: "", email: "", state: "", zip: "",
    consent: false,
  };
}

const RULED = new Set([
  "incident_age_bucket", "physically_injured", "medical_treatment", "at_fault",
  "at_fault_party_insured", "currently_represented", "accident_description",
]);

// Apply admin required/optional rules: optional questions get a Skip button.
const applyRule = (s, req) => {
  if (s.kind === "contact") return { ...s, zipOptional: !req("zip") };
  if (!RULED.has(s.field)) return s;
  const r = req(s.field);
  if (s.kind === "text") return { ...s, skippable: !r, required: r, subtitle: r ? "A sentence or two helps the attorney." : s.subtitle };
  return { ...s, skippable: !r };
};

export function buildSteps(slug, isMva, req = () => true) {
  return [
    { key: "first", kind: "cards", ...FIRST_STEP[slug] },
    { key: "when", kind: "choices", field: "incident_age_bucket", title: "When did it happen?", subtitle: "Recent cases are easier to build.", options: AGE_OPTIONS },
    { key: "injured", kind: "choices", field: "physically_injured", title: "Were you hurt?", subtitle: "Pain that showed up later counts too.", options: YES_NO },
    { key: "treated", kind: "choices", field: "medical_treatment", title: "Did you get medical care?", subtitle: "ER, urgent care, a doctor or a chiropractor all count.", options: YES_NO },
    {
      key: "fault", kind: "choices", field: "at_fault", title: "Who caused it?",
      options: [{ value: false, label: "Someone else" }, { value: true, label: "Mostly me" }],
    },
    isMva && {
      key: "insured", kind: "choices", field: "at_fault_party_insured", title: "Did the other driver have insurance?",
      options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }, { value: "unknown", label: "Not sure" }],
    },
    {
      key: "lawyer", kind: "choices", field: "currently_represented", title: "Already working with a lawyer on this?",
      options: [{ value: false, label: "No, not yet" }, { value: true, label: "Yes, I have one" }],
    },
    { key: "story", kind: "text", field: "accident_description", title: "Tell us a little about it", subtitle: "Optional — a sentence or two helps the attorney.", skippable: true },
    { key: "contact", kind: "contact", title: "Where should the attorney reach you?", subtitle: "Only used to review your case." },
    { key: "consent", kind: "consent", title: "Last step — send your case", subtitle: "Agree below so an attorney can contact you." },
  ].filter(Boolean).map((s) => applyRule(s, req));
}

const AGE_POINTS = {
  within_30_days: 18, "31_to_60_days": 15, "61_to_90_days": 12, "91_days_to_1_year": 8, over_1_year: 3,
};

export function caseScore(a, slug) {
  let s = a[FIRST_STEP[slug].field] ? 12 : 0;
  s += AGE_POINTS[a.incident_age_bucket] || 0;
  if (a.physically_injured === true) s += 18;
  if (a.medical_treatment === true) s += 15;
  if (a.at_fault === false) s += 15;
  if (a.at_fault === true) s -= 10;
  if (a.at_fault_party_insured === "yes") s += 8;
  if (a.at_fault_party_insured === "unknown") s += 4;
  if (a.currently_represented === false) s += 10;
  if (a.currently_represented === true) s -= 15;
  return Math.max(0, Math.min(96, s));
}

export function strengthLabel(score) {
  if (score <= 0) return "Let's see";
  if (score < 30) return "Checking";
  if (score < 55) return "Promising";
  if (score < 80) return "Strong";
  return "Very strong";
}

export function contactValid(a, zipRequired = true) {
  const zip = a.zip.trim() ? isValidZip(a.zip) : !zipRequired;
  return Boolean(
    a.first_name.trim() && a.last_name.trim() && isValidUSPhone(a.phone) && a.state
    && a.email.trim() && isValidEmail(a.email) && zip,
  );
}

export const tcpaText = (v) => siteText((v.tcpaLanguage && v.tcpaLanguage.trim()) ? v.tcpaLanguage : TCPA_LANGUAGE);

export function buildPayload({ a, slug, isMva, session, turnstileToken, vertical, params }) {
  const first = FIRST_STEP[slug];
  const pick = first.field === "detail" ? first.options.find((o) => o.value === a.detail) : null;
  const description = [pick ? `${first.detailLabel}: ${pick.label}.` : "", a.accident_description.trim()]
    .filter(Boolean).join(" ");
  const trustedform_url = vertical.isSamplePage ? null : readTrustedFormCert();
  return {
    aff: params.get("aff") || null,
    sub_id: params.get("sub") || params.get("sub_id") || null,
    landing_page: vertical.slug,
    case_type: a.case_type || null,
    first_name: a.first_name.trim(),
    last_name: a.last_name.trim(),
    phone: a.phone.trim(),
    email: a.email.trim() || null,
    state: a.state,
    zip: a.zip.trim() || null,
    incident_date: null,
    incident_age_bucket: a.incident_age_bucket || null,
    at_fault: a.at_fault,
    currently_represented: a.currently_represented,
    physically_injured: a.physically_injured,
    medical_treatment: a.medical_treatment,
    at_fault_party_insured: isMva ? (a.at_fault_party_insured || null) : null,
    accident_description: description || null,
    police_report_number: null,
    accident_photo_ids: [],
    consent: a.consent,
    tcpa_language: tcpaText(vertical),
    trustedform_url,
    landing_url: window.location.href,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent,
    turnstile_token: turnstileToken || null,
    company: null,
    load_ms: session ? session.elapsedMs() : null,
    form_experience: "case_quiz",
    experiment_id: null,
    experiment_variant: "case_quiz",
    quiz_session_id: session?.sessionId || null,
    buyer_code: vertical.buyerCode || null,
    buyer_source: vertical.buyerSource || null,
  };
}
